/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2026 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////
<template>
  <div
      class="sliding-search-panel"
      :class="[positionClass, { 'is-open': isOpen }]"
      :style="panelStyles"
      ref="panelContainer"
  >
    <div class="panel-content shadow bg-white border">

      <div class="d-flex justify-content-between align-items-center mb-2 px-1">
        <small class="text-muted text-uppercase fw-bold" style="font-size: 0.7rem;">Wyszukiwanie</small>
        <button class="btn btn-sm btn-link text-secondary p-0" @click="close">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="slot-wrapper">
        <slot></slot>
      </div>
    </div>

    <div
        class="panel-handle shadow-sm text-white d-flex align-items-center justify-content-center"
        :class="handleColorClass"
        @click="toggle"
        title="Otwórz wyszukiwarkę"
    >
      <i class="fas" :class="iconClass"></i>
    </div>
  </div>
</template>

<script>
export default {
  name: "SlidingSearchPanel",
  props: {
    // Pozycja: 'left', 'right', 'top', 'bottom'
    position: {
      type: String,
      default: "left",
      validator: (value) => ["left", "right", "top", "bottom"].includes(value)
    },
    handleColor: {
      type: String,
      default: "bg-primary"
    },
    // Używane dla left/right (odległość od góry)
    topOffset: {
      type: String,
      default: "120px"
    },
    // Używane dla top/bottom (odległość od lewej)
    leftOffset: {
      type: String,
      default: "50px"
    },
    // Szerokość panelu (np. '600px', '100%', '30vw')
    panelWidth: {
      type: String,
      default: "600px"
    },
    blockOpened:{
      type: Boolean,
      default: false
    }
  },
  emits: ['onToggle'],
  data() {
    return {
      isOpen: false
    };
  },
  computed: {
    positionClass() {
      return `panel-${this.position}`;
    },
    handleColorClass() {
      return this.handleColor;
    },
    // Dynamiczne style inline zależne od pozycji i propsów
    panelStyles() {
      const styles = {
        width: this.panelWidth
      };

      // Jeśli panel boczny -> ustawiamy top
      if (['left', 'right'].includes(this.position)) {
        styles.top = this.topOffset;
      }
      // Jeśli panel pionowy -> ustawiamy left
      else if (['top', 'bottom'].includes(this.position)) {
        styles.left = this.leftOffset;
      }

      return styles;
    },
    // Ikona strzałki zależna od pozycji i stanu
    iconClass() {
      if (!this.isOpen) return 'fa-search';

      switch (this.position) {
        case 'left': return 'fa-chevron-left';
        case 'right': return 'fa-chevron-right';
        case 'top': return 'fa-chevron-up';
        case 'bottom': return 'fa-chevron-down';
        default: return 'fa-times';
      }
    }
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
      this.$emit('onToggle', this.isOpen);
    },
    open() {
      this.isOpen = true;
    },
    close() {
      this.isOpen = false;
    },
    handleClickOutside(event) {
      if(this.blockOpened) return;

      if (this.isOpen && this.$refs.panelContainer && !this.$refs.panelContainer.contains(event.target)) {
        this.close();
      }
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  }
};
</script>

<style scoped>
.sliding-search-panel {
  position: fixed;
  z-index: 100999;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: flex-start;
  /* width jest sterowany przez style inline (prop panelWidth) */
}

.panel-content {
  flex-grow: 1;
  padding: 15px;
  background-color: white;
  min-height: 80px;
  width: 100%;
}

.panel-handle {
  /* Stały wymiar uchwytu - potrzebny do obliczeń calc() */
  width: 45px;
  height: 45px;
  cursor: pointer;
  font-size: 1.2rem;
  transition: background-color 0.2s;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  flex-shrink: 0; /* Nie zgniataj uchwytu */
}

.panel-handle:hover {
  filter: brightness(1.1);
}

.slot-wrapper {
  width: 100%;
}

/* =========================================
   1. POZYCJA: LEFT
   ========================================= */
.sliding-search-panel.panel-left {
  left: 0;
  /* Przesuń w lewo o 100%, ale wystaw 45px */
  transform: translateX(calc(-100% + 45px));
  flex-direction: row; /* Content | Handle */
}
.sliding-search-panel.panel-left.is-open {
  transform: translateX(0);
}
.sliding-search-panel.panel-left .panel-content {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}
.sliding-search-panel.panel-left .panel-handle {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  margin-top: 10px;
}

/* =========================================
   2. POZYCJA: RIGHT
   ========================================= */
.sliding-search-panel.panel-right {
  right: 0;
  /* Przesuń w prawo o 100%, ale wystaw 45px */
  transform: translateX(calc(100% - 45px));
  flex-direction: row-reverse; /* Handle | Content */
}
.sliding-search-panel.panel-right.is-open {
  transform: translateX(0);
}
.sliding-search-panel.panel-right .panel-content {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}
.sliding-search-panel.panel-right .panel-handle {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  margin-top: 10px;
}

/* =========================================
   3. POZYCJA: TOP
   ========================================= */
.sliding-search-panel.panel-top {
  top: 0;
  /* Przesuń w górę o 100%, ale wystaw 45px */
  transform: translateY(calc(-100% + 45px));
  flex-direction: column; /* Content (top) | Handle (bottom) */
  align-items: flex-start; /* Domyślne wyrównanie do lewej (zdefiniowane przez leftOffset) */
}
.sliding-search-panel.panel-top.is-open {
  transform: translateY(0);
}
.sliding-search-panel.panel-top .panel-content {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}
.sliding-search-panel.panel-top .panel-handle {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  margin-left: 10px; /* Odsunięcie uchwytu od lewej krawędzi panelu */
}

/* =========================================
   4. POZYCJA: BOTTOM
   ========================================= */
.sliding-search-panel.panel-bottom {
  bottom: 0;
  /* Przesuń w dół o 100%, ale wystaw 45px */
  transform: translateY(calc(100% - 45px));
  flex-direction: column-reverse; /* Handle (top) | Content (bottom) */
  align-items: flex-start;
}
.sliding-search-panel.panel-bottom.is-open {
  transform: translateY(0);
}
.sliding-search-panel.panel-bottom .panel-content {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}
.sliding-search-panel.panel-bottom .panel-handle {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  margin-left: 10px;
}
</style>